export class DemoPackError extends Error {
    status;
    code;
    details;
    hint;
    constructor(message, options = {}) {
        super(message);
        this.name = 'DemoPackError';
        this.status = options.status ?? 400;
        this.code = options.code ?? 'DMPACK';
        this.details = options.details ?? null;
        this.hint = options.hint ?? null;
    }
    toJSON() {
        return {
            code: this.code,
            details: this.details,
            hint: this.hint,
            message: this.message,
        };
    }
    toResponse() {
        return new Response(JSON.stringify(this.toJSON()), {
            status: this.status,
            statusText: statusTextFor(this.status),
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }
}
export function unsupported(message, details = null) {
    return new DemoPackError(message, {
        status: 400,
        code: 'DMPACK_UNSUPPORTED',
        details,
        hint: 'See docs/COMPATIBILITY.md for the supported DemoPack operation matrix.',
    });
}
export function invalidInput(message, details = null) {
    return new DemoPackError(message, {
        status: 400,
        code: 'DMPACK_INPUT',
        details,
    });
}
export function initializationError(cause) {
    const details = cause instanceof Error ? cause.message : 'Unknown initialization failure.';
    return new DemoPackError(`DemoPack failed to initialize: ${details}`, {
        status: 500,
        code: 'DMPACK_INIT',
        details,
        hint: 'Schema and seed must apply atomically. A failed fixture is not presented as ready.',
    });
}
export function lifecycleError(message, state) {
    return new DemoPackError(message, {
        status: 409,
        code: 'DMPACK_LIFECYCLE',
        details: state,
        hint: 'DemoPack operations are bound to one page-instance lifecycle. Reset discards in-flight writes.',
    });
}
export function staleOperationError() {
    return lifecycleError('DemoPack discarded a stale operation after reset or dispose.', 'stale');
}
export function cardinalityError(rowCount) {
    return new DemoPackError('JSON object requested, multiple (or no) rows returned', {
        status: 406,
        code: 'PGRST116',
        details: `Results contain ${rowCount} rows, application/vnd.pgrst.object+json requires 1 row`,
        hint: null,
    });
}
export function fromEngineError(error) {
    const record = asRecord(error);
    const code = stringField(record, 'code') ?? 'DMPACK_ENGINE';
    const message = stringField(record, 'message') ?? (error instanceof Error ? error.message : 'Database engine rejected the request.');
    const details = stringField(record, 'detail') ?? stringField(record, 'details');
    return new DemoPackError(message, {
        status: statusForEngineCode(code),
        code,
        details,
        hint: stringField(record, 'hint'),
    });
}
function statusForEngineCode(code) {
    if (code === '23505' || code === '23503') {
        return 409;
    }
    if (code === '23502' || code === '23514' || code === '22P02' || code === '22001') {
        return 400;
    }
    return 400;
}
function asRecord(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
    }
    return null;
}
function stringField(record, key) {
    if (!record) {
        return null;
    }
    const value = record[key];
    return typeof value === 'string' && value.length > 0 ? value : null;
}
export function statusTextFor(status) {
    switch (status) {
        case 200:
            return 'OK';
        case 201:
            return 'Created';
        case 204:
            return 'No Content';
        case 206:
            return 'Partial Content';
        case 400:
            return 'Bad Request';
        case 406:
            return 'Not Acceptable';
        case 409:
            return 'Conflict';
        case 416:
            return 'Range Not Satisfiable';
        default:
            return '';
    }
}
